import type { RealtimeAdapter, RealtimeSubscription, RealtimeMsg, RealtimePayload } from "./types";

interface MockSubscriptionContext {
  subscription: RealtimeSubscription;
  publish: (topic: string, msg: RealtimePayload) => void;
  triggerError: (error: Error) => void;
  messages: RealtimeMsg[];
}

type MockPublishListener = (topic: string, payload: RealtimePayload) => void;

export class MockNatsAdapter implements RealtimeAdapter {
  #topicPublishers = new Map<string, (topic: string, msg: RealtimePayload) => void>();
  #subscriptions = new Map<string, MockSubscriptionContext>();
  #messageHistory: { topic: string; payload: RealtimePayload }[] = [];
  #publishListeners = new Set<MockPublishListener>();
  #initError: Error | null = null;
  #initFailuresRemaining = 0;

  onConnectionExhausted?: () => void;
  onInitError?: () => void;
  onReconnect?: (disconnectMs: number) => void;
  onConnect?: () => void;

  /**
   * NOTE: Unlike the real NatsAdapter, this mock does NOT automatically fire
   * onConnect during init(). Tests that need to exercise the onConnect path
   * (e.g., recovery budget refund) must call simulateConnect() explicitly.
   * See simulateReconnect() for the analogous pattern.
   */
  init(): Promise<void> {
    if (this.#initFailuresRemaining > 0) {
      this.#initFailuresRemaining--;
      throw this.#initError ?? new Error("Mock init failure");
    }
    if (this.#initError) {
      throw this.#initError;
    }
    return Promise.resolve();
  }

  subscribe(topic: string): RealtimeSubscription {
    const existing = this.#subscriptions.get(topic);
    if (existing) {
      return existing.subscription;
    }

    const context = this.#createMockSubscription();
    this.#subscriptions.set(topic, context);
    this.#topicPublishers.set(topic, context.publish);

    return context.subscription;
  }

  unsubscribe(topic: string): void {
    this.#subscriptions.delete(topic);
    this.#topicPublishers.delete(topic);
  }

  close(): Promise<void> {
    this.#subscriptions.clear();
    this.#topicPublishers.clear();
    return Promise.resolve();
  }

  publish(topic: string, message: RealtimePayload | Uint8Array): void {
    // Support both RealtimePayload (test convenience) and Uint8Array (adapter interface)
    const payload =
      message instanceof Uint8Array ? (JSON.parse(new TextDecoder().decode(message)) as RealtimePayload) : message;
    this.#messageHistory.push({ topic, payload });

    const publisher = this.#topicPublishers.get(topic);
    if (publisher) {
      publisher(topic, payload);
    }

    for (const listener of [...this.#publishListeners]) {
      listener(topic, payload);
    }
  }

  /**
   * Observe every `publish` made through this adapter, including the app's own
   * outbound traffic. Added for the Playwright cross-device bridge, which relays
   * one page's publishes into another page's adapter (see `playwright-bridge.ts`).
   */
  addPublishListener(listener: MockPublishListener): () => void {
    this.#publishListeners.add(listener);
    return () => {
      this.#publishListeners.delete(listener);
    };
  }

  getSubscriptions(): string[] {
    return Array.from(this.#subscriptions.keys());
  }

  getMessageHistory(topic?: string): RealtimePayload[] {
    if (topic) {
      return this.#messageHistory.filter((entry) => entry.topic === topic).map((entry) => entry.payload);
    }
    return this.#messageHistory.map((entry) => entry.payload);
  }

  clearHistory(): void {
    this.#messageHistory = [];
    this.#initError = null;
  }

  simulateError(error: Error): void {
    this.#initError = error;
  }

  simulateTransientError(error: Error, failureCount: number): void {
    this.#initError = error;
    this.#initFailuresRemaining = failureCount;
  }

  isSubscribed(topic: string): boolean {
    return this.#subscriptions.has(topic);
  }

  simulateConnectionExhausted(): void {
    this.#subscriptions.clear();
    this.#topicPublishers.clear();
    this.onConnectionExhausted?.();
  }

  simulateConnectionInitError(): void {
    this.onInitError?.();
  }

  simulateReconnect(disconnectMs = 0): void {
    this.onReconnect?.(disconnectMs);
  }

  simulateConnect(): void {
    this.onConnect?.();
  }

  simulateSubscriptionError(topic: string, error: Error): void {
    const context = this.#subscriptions.get(topic);
    if (context) {
      context.triggerError(error);
    }
  }

  #createMockSubscription(): MockSubscriptionContext {
    const messages: RealtimeMsg[] = [];
    let resolveNext: ((value: IteratorResult<RealtimeMsg>) => void) | null = null;
    let rejectNext: ((error: Error) => void) | null = null;

    function publish(topic: string, msg: RealtimePayload): void {
      const raw = JSON.stringify(msg);
      const data = new TextEncoder().encode(raw);
      const message = { subject: topic, data };

      if (resolveNext) {
        resolveNext({ value: message, done: false });
        resolveNext = null;
        rejectNext = null;
      } else {
        messages.push(message);
      }
    }

    function triggerError(error: Error): void {
      if (rejectNext) {
        rejectNext(error);
        rejectNext = null;
        resolveNext = null;
      }
    }

    const subscription: RealtimeSubscription = {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      unsubscribe: () => {},
      drain: () => Promise.resolve(),
      [Symbol.asyncIterator]: () => {
        return {
          next: (): Promise<IteratorResult<RealtimeMsg>> => {
            if (messages.length > 0) {
              const msg = messages.shift()!;
              return Promise.resolve({ value: msg, done: false });
            }
            return new Promise<IteratorResult<RealtimeMsg>>((resolve, reject) => {
              resolveNext = resolve;
              rejectNext = reject;
            });
          },
        };
      },
    };

    return { subscription, publish, triggerError, messages };
  }
}
